"use client";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TimeEntryForm } from "@/components/TimeEntryForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { showSuccess, showError } from "@/utils/toast";
import { generateInvoiceFromRecentBillableTime } from "@/utils/invoicing";
import { useNavigate } from "react-router-dom";

const TimeTrackingPage = () => {
  const { profile, company, getFeatureAccess } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  // NEW: Redirect client-role users to Client Portal
  useEffect(() => {
    if (profile?.role === 'client') {
      navigate('/client-portal', { replace: true });
    }
  }, [profile, navigate]);

  // Hide time tracking management for client-role users
  const canManageTimeTracking = profile?.role !== 'client';

  // Check if this is IBP consulting tenant
  const isIBPConsulting = getFeatureAccess('ibp_consulting');

  const { data: timeLogs, isLoading } = useQuery({
    queryKey: ["time_logs", profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const { data } = await supabase
        .from("time_logs")
        .select("*, tasks(title), projects(name), clients(name)")
        .eq("company_id", profile.company_id)
        .order("start_time", { ascending: false });
      return data || [];
    },
  });

  const handleTimeLogged = () => {
    setShowForm(false);
    queryClient.invalidateQueries({ queryKey: ["time_logs"] });
    showSuccess(isIBPConsulting ? "Client time logged successfully!" : "Time logged successfully!");
  };

  const generateInvoiceFromTime = async () => {
    if (!company?.id) return;
    const invoice = await generateInvoiceFromRecentBillableTime(company.id);
    if (!invoice) {
      showSuccess(isIBPConsulting ? "No recent billable time found for client. Log some time first!" : "No recent billable time found. Log some time first!");
      return;
    }
    showSuccess(isIBPConsulting ? `Client invoice #${invoice.invoice_number} created from billable time!` : `Invoice #${invoice.invoice_number} created from billable time!`);
    queryClient.invalidateQueries({ queryKey: ["invoices"] });
  };

  if (isLoading) return <div className="p-4">Loading time logs...</div>;

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">{isIBPConsulting ? "Client Time Tracking" : "Time Tracking"}</h1>
        {canManageTimeTracking && (
          <>
            <Button onClick={() => setShowForm(!showForm)} className="mt-4">
              <Clock className="mr-2 h-4 w-4" />
              {showForm ? "Cancel" : "Log Time"}
            </Button>
            <Button 
              variant="outline" 
              onClick={generateInvoiceFromTime} 
              className="mt-2 ml-2"
              disabled={timeLogs?.filter((log: any) => log.billable).length === 0}
            >
              <DollarSign className="mr-2 h-4 w-4" />
              {isIBPConsulting ? "Generate Client Invoice from Time" : "Generate Invoice from Time"}
            </Button>
          </>
        )}
      </div>
      {canManageTimeTracking && showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{isIBPConsulting ? "Log Client Time Entry" : "Log New Time Entry"}</CardTitle>
          </CardHeader>
          <CardContent>
            <TimeEntryForm onSuccess={handleTimeLogged} />
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {timeLogs?.slice(0, 5).map((log: any) => (
              <div key={log.id} className="flex justify-between items-center py-2 border-b last:border-b-0">
                <div>
                  <p className="font-medium">{log.description}</p>
                  <p className="text-sm text-muted-foreground">
                    {log.projects?.name || "No Project"} / {log.tasks?.title || "No Task"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {log.clients?.name ? `Client: ${log.clients.name}` : 'Non-billable'}
                  </p>
                </div>
                <div className="text-right">
                  <Badge>{log.duration_minutes} min</Badge>
                  {log.billable && <Badge variant="default" className="ml-1">Billable</Badge>}
                </div>
              </div>
            ))}
            {timeLogs?.length === 0 && <p className="text-muted-foreground text-center py-8">No time entries yet. Log your first one!</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Weekly Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{Math.round(timeLogs?.reduce((sum: number, log: any) => sum + (log.duration_minutes || 0), 0) / 60)}h total</p>
            <p className="text-sm text-muted-foreground">Billable: {timeLogs?.filter((log: any) => log.billable).length} entries</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TimeTrackingPage;